import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Badge, Button, Skeleton } from '@/components/ui';
import { PoolPageTypeNameService } from '@/services/poolPageTypeName.service';
import { useToast } from '@/contexts/ToastContext';
import type { PoolPageTypeName, PoolInfo } from '@/types/poolPageTypeName.types';
import { Search, Save, X, ChevronDown, ChevronRight } from 'lucide-react';

function EditPanel({
  allPools,
  editPoolIds,
  saving,
  onTogglePool,
  onCancel,
  onSave,
}: {
  allPools: PoolInfo[];
  editPoolIds: number[];
  saving: boolean;
  onTogglePool: (poolId: number) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-600 dark:bg-dark-700">
      <p className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
        Select pools to restrict access (leave empty for no restrictions):
      </p>
      <div className="max-h-48 overflow-y-auto">
        {allPools.length === 0 ? (
          <p className="text-sm text-gray-400">No pools available</p>
        ) : (
          <div className="space-y-2">
            {allPools.map((pool) => (
              <label
                key={pool.poolId}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-dark-600"
              >
                <input
                  type="checkbox"
                  checked={editPoolIds.includes(pool.poolId)}
                  onChange={() => onTogglePool(pool.poolId)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{pool.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={saving}
          leftIcon={<X className="h-3.5 w-3.5" />}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={saving}
          leftIcon={<Save className="h-3.5 w-3.5" />}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function PoolPageTypeNameModal({ isOpen, onClose }: Props) {
  const { showToast } = useToast();
  const [data, setData] = useState<PoolPageTypeName[]>([]);
  const [allPools, setAllPools] = useState<PoolInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editPoolIds, setEditPoolIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [pageTypeNames, pools] = await Promise.all([
        PoolPageTypeNameService.getPoolPageTypeNames(),
        PoolPageTypeNameService.getAvailablePools(),
      ]);
      setData(pageTypeNames);
      setAllPools(pools);
    } catch {
      showToast('Failed to load data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setEditingRow(null);
      setSearch('');
    }
  }, [isOpen, loadData]);

  const filtered = useMemo(() => {
    if (!search) return data;
    const term = search.toLowerCase();
    return data.filter((item) => item.pageTypeName.toLowerCase().includes(term));
  }, [data, search]);

  const handleEdit = (item: PoolPageTypeName) => {
    setEditingRow(item.pageTypeName);
    setEditPoolIds(item.pools?.map((p) => p.poolId) ?? []);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditPoolIds([]);
  };

  const handleTogglePool = (poolId: number) => {
    setEditPoolIds((prev) =>
      prev.includes(poolId) ? prev.filter((id) => id !== poolId) : [...prev, poolId]
    );
  };

  const handleSave = async () => {
    if (!editingRow) return;
    try {
      setSaving(true);
      await PoolPageTypeNameService.updatePoolPageTypeName({
        pageTypeName: editingRow,
        poolIds: editPoolIds.length > 0 ? editPoolIds : null,
      });
      // Update local data
      setData((prev) =>
        prev.map((item) =>
          item.pageTypeName === editingRow
            ? {
                ...item,
                pools:
                  editPoolIds.length > 0
                    ? editPoolIds.map(
                        (id) => allPools.find((p) => p.poolId === id) ?? { poolId: id, name: '' }
                      )
                    : null,
              }
            : item
        )
      );
      showToast(`Updated pools for ${editingRow}`, { variant: 'success' });
      setEditingRow(null);
      setEditPoolIds([]);
    } catch {
      showToast('Failed to update pool assignments', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Page Type Names"
      size="xl"
      showCloseButton
    >
      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search page type names..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-700 dark:text-white dark:placeholder-gray-500"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3 pb-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          {search ? 'No page type names match your search' : 'No page type names available'}
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto pb-4">
          {/* Desktop table */}
          <table className="hidden w-full md:table">
            <thead className="sticky top-0 z-10 bg-white dark:bg-dark-800">
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-dark-600 dark:text-gray-400">
                <th className="pb-3 pr-4">Page Type Name</th>
                <th className="pb-3 pr-4">Assigned Pools</th>
                <th className="w-20 pb-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filtered.map((item) => (
                <React.Fragment key={item.pageTypeName}>
                  <tr className="group">
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        className="flex items-center gap-2"
                        onClick={() =>
                          editingRow === item.pageTypeName ? handleCancelEdit() : handleEdit(item)
                        }
                      >
                        {editingRow === item.pageTypeName ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {item.pageTypeName}
                        </span>
                      </button>
                    </td>
                    <td className="py-3 pr-4">
                      {item.pools && item.pools.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.pools.map((pool) => (
                            <Badge key={pool.poolId} variant="primary" size="sm">
                              {pool.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs italic text-gray-400 dark:text-gray-500">
                          No restrictions
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      {editingRow !== item.pageTypeName && (
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                  {editingRow === item.pageTypeName && (
                    <tr>
                      <td colSpan={3} className="pb-4 pt-1">
                        <EditPanel
                          allPools={allPools}
                          editPoolIds={editPoolIds}
                          saving={saving}
                          onTogglePool={handleTogglePool}
                          onCancel={handleCancelEdit}
                          onSave={handleSave}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Mobile card list */}
          <div className="space-y-3 md:hidden">
            {filtered.map((item) => (
              <div
                key={item.pageTypeName}
                className="rounded-lg border border-gray-200 bg-white dark:border-dark-600 dark:bg-dark-800"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between p-3"
                  onClick={() =>
                    editingRow === item.pageTypeName ? handleCancelEdit() : handleEdit(item)
                  }
                >
                  <div className="flex items-center gap-2">
                    {editingRow === item.pageTypeName ? (
                      <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.pageTypeName}
                    </span>
                  </div>
                  {item.pools && item.pools.length > 0 ? (
                    <span className="ml-2 flex-shrink-0 text-xs text-primary-600 dark:text-primary-400">
                      {item.pools.length} pool{item.pools.length !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="ml-2 flex-shrink-0 text-xs italic text-gray-400">Open</span>
                  )}
                </button>
                {editingRow !== item.pageTypeName && item.pools && item.pools.length > 0 && (
                  <div className="flex flex-wrap gap-1 border-t border-gray-100 px-3 py-2 dark:border-dark-700">
                    {item.pools.map((pool) => (
                      <Badge key={pool.poolId} variant="primary" size="sm">
                        {pool.name}
                      </Badge>
                    ))}
                  </div>
                )}
                {editingRow === item.pageTypeName && (
                  <div className="border-t border-gray-100 p-3 dark:border-dark-700">
                    <EditPanel
                      allPools={allPools}
                      editPoolIds={editPoolIds}
                      saving={saving}
                      onTogglePool={handleTogglePool}
                      onCancel={handleCancelEdit}
                      onSave={handleSave}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
